import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Teacher, Mastery } from 'services';
import { ModelsActions } from "store/actions";
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import { Module } from 'services';
import snakeToCamel from 'utils/snakeToTitle';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

const schema = {
  name_en: {
    presence: { allowEmpty: false, message: "is required" }
  },
  specialisation: {
    presence: { allowEmpty: false, message: "is required" }
  },
};

const messages = {

}

const fields = [
  { field: "name_en", label: "Name", required: true, width: 6 },
  { field: "title", label: "Title", required: false, width: 6 },
  { field: "description", label: "Description", required: false, width: 12, multiline: true },
  { field: "specialisation", label: "Specialisation", required: true, width: 6, type: "dropdown", options: [...Module.SPECIALISATIONS.map(s => ({ value: s, label: snakeToCamel(s) }))] },
]

let mounted = false;

const TeacherEdit = props => {
  const { open, onClose, className, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.teacher);
  const dispatch = useDispatch();
  const [formState, setFormState] = useState({
    values: { ...model }
  });
  const [masteries, setMasteries] = useState([]);
  const { values } = formState;

  const load_masteries = async () => {
    setLoading(true);
    await runAsyncBlock(async () => {
      const { models } = await Mastery.list({ query: { limit: 9999 } });
      if (mounted) {
        setMasteries(models);
      }
    }).finally(() => { mounted && setLoading(false) });
  }


  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  useEffect(() => {
    mounted = true;
    load_masteries();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!open) {
    return null;
  }


  const handleClose = event => {
    event.preventDefault();

    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const response = await Teacher.update(model.id,
        {
          ...formState.values,
        });
      dispatch(ModelsActions.update_model({ teacher: response.model }));
    }).finally(() => {
      setLoading(false);
      onClose();
    });

  }

  const handleChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value
      }
    }));
  };

  const hasError = field => formState.errors[field] ? true : false;

  const renderFields = () => {
    return fields.map(the_field => {
      let { field, label, required, type, options, width, multiline } = the_field;
      if (type === "dropdown") return (
        <Grid key={field} item md={width} xs={width}>
          <TextField
            fullWidth
            label={label}
            name={field}
            helperText={
              hasError(field) ? formState.errors[field][0] : null
            }
            onChange={handleChange}
            select
            // eslint-disable-next-line react/jsx-sort-props
            SelectProps={{ native: true }}
            value={values[field] || ""}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            required={required}
            error={hasError(field)}
          >
            <option
              disabled
              value=""
            />
            {options.map(option => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </TextField>
        </Grid>
      );

      return (
        <Grid key={field} item md={width} xs={width}>
          <TextField
            fullWidth
            label={label}
            helperText={
              hasError(field) ? formState.errors[field][0] : null
            }
            multiline={multiline}
            name={field}
            required={required}
            onChange={handleChange}
            value={values[field] || ""}
            error={hasError(field)}
            variant="outlined" />
        </Grid>
      )
    })
  }

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Edit Teacher
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              {renderFields()}
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Mastery Level"
                  name="mastery_level_id"
                  helperText={
                    hasError("mastery_level_id") ? formState.errors["mastery_level_id"][0] : null
                  }
                  onChange={handleChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={values["mastery_level_id"] || ""}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  required={true}
                  error={hasError("mastery_level_id")}
                >
                  <option
                    disabled
                    value=""
                  />
                  {masteries.map(option => (
                    <option
                      key={option.id}
                      value={option.id}
                    >
                      {option.name_en}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <ErrorMessage message={errorMessage} map={messages} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Save</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
};

TeacherEdit.propTypes = {
  className: PropTypes.string,
  model: PropTypes.any,
  setModel: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

TeacherEdit.defaultProps = {
  open: false,
  onClose: () => { }
};

export default TeacherEdit;
